import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { attemptOpenDesktopApp } from '@/lib/utils/app-links'
import { extractOrgSlug } from '@/utils/org-paths'
import { DesktopIcon, XIcon } from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

/**
 * Persisted choice for the desktop handoff:
 * - 'always': user opened the app before — auto-attempt the deep link on future visits
 * - 'never': user declined — never prompt again
 * - unset: first visit — show the prompt
 */
const PREFERENCE_KEY = 'scrambl:desktop-app-preference'

/** Prevents re-prompting/re-redirecting on every navigation within a session */
const SESSION_KEY = 'scrambl:desktop-prompt-handled'

type DesktopPreference = 'always' | 'never' | null

function getPreference(): DesktopPreference {
  const value = localStorage.getItem(PREFERENCE_KEY)
  return value === 'always' || value === 'never' ? value : null
}

/**
 * OpenInDesktopPrompt - shown only in the browser (never inside Tauri).
 *
 * On a user's first visit to an org page it offers to open the page in the
 * Scrambl desktop app via the scrambl:// deep link. If they accept (and the
 * app opens), future visits hand off automatically; if they decline, they are
 * never asked again.
 */
export function OpenInDesktopPrompt() {
  const location = useLocation()
  const [visible, setVisible] = useState(false)
  const [isOpening, setIsOpening] = useState(false)
  const hasRun = useRef(false)

  const currentPath = location.pathname + location.search
  const isOrgPage = extractOrgSlug(location.pathname) !== null

  useEffect(() => {
    if (hasRun.current || !isOrgPage) return
    if (sessionStorage.getItem(SESSION_KEY)) return

    const preference = getPreference()
    if (preference === 'never') return

    hasRun.current = true
    sessionStorage.setItem(SESSION_KEY, 'true')

    if (preference === 'always') {
      // Returning user who opted in — hand off to the desktop app right away.
      // The browser may require a gesture for custom protocols, so keep the
      // banner up as a manual fallback until the handoff succeeds.
      setIsOpening(true)
      setVisible(true)
      void attemptOpenDesktopApp(currentPath).then(didOpen => {
        setIsOpening(false)
        if (didOpen) setVisible(false)
      })
    } else {
      setVisible(true)
    }
  }, [isOrgPage, currentPath])

  if (!visible) return null

  const handleOpen = async () => {
    setIsOpening(true)
    const didOpen = await attemptOpenDesktopApp(currentPath)
    setIsOpening(false)
    if (didOpen) {
      localStorage.setItem(PREFERENCE_KEY, 'always')
      setVisible(false)
    } else {
      toast.error(
        "Couldn't open the Scrambl desktop app. Make sure it's installed.",
      )
    }
  }

  const handleNever = () => {
    localStorage.setItem(PREFERENCE_KEY, 'never')
    setVisible(false)
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border bg-background p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <div className="bg-primary/10 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md">
          <DesktopIcon className="text-primary h-5 w-5" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {isOpening ? 'Opening in the desktop app…' : 'Open in the desktop app?'}
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Scrambl works best in the desktop app. This page can open there
            automatically.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setVisible(false)}
          className="text-muted-foreground -mr-1 -mt-1 h-6 w-6 flex-shrink-0"
          title="Dismiss"
        >
          <XIcon className="h-3.5 w-3.5" />
          <span className="sr-only">Dismiss</span>
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => void handleOpen()}
          disabled={isOpening}
          className="h-7 text-xs"
        >
          {isOpening ? 'Opening…' : 'Open app'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNever}
          className="text-muted-foreground h-7 text-xs"
        >
          Don&apos;t ask again
        </Button>
      </div>
    </div>
  )
}
